import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Fragment } from 'react';
import { cn } from '../lib/utils.ts';

import {
  insertOrderWord,
  moveOrderWordToSlot,
  removeOrderWord,
  reorderOrderWords,
  toggleOrderWord,
  type OrderWordItem,
} from '../lib/order-words.ts';

type Props = {
  canonicalWorkspace?: OrderWordItem[];
  disabled?: boolean;
  items: OrderWordItem[];
  onWorkspaceChange(workspace: OrderWordItem[]): void;
  workspaceItemStatus?: (
    item: OrderWordItem,
    position: number,
  ) => 'correct' | 'incorrect' | undefined;
  workspace: OrderWordItem[];
};

function DraggableWord({
  item,
  disabled,
  shortcut,
  used,
  onChoose,
}: {
  item: OrderWordItem;
  onChoose(): void;
  shortcut: number;
  used: boolean;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `top-${item.sourceIndex}`,
    disabled: used || disabled,
  });
  const dragProps = used || disabled ? {} : { ...attributes, ...listeners };
  return (
    <button
      {...dragProps}
      className={cn(
        'inline-flex min-h-8 items-center gap-1 rounded-full border border-border bg-card px-1.5 py-1 text-start text-card-foreground',
        used && 'border-dashed opacity-50',
      )}
      disabled={disabled}
      onClick={onChoose}
      ref={setNodeRef}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
      }}
      type="button"
    >
      <span className="inline-grid size-5 place-items-center rounded border border-border font-mono text-xs text-muted-foreground">
        {shortcut}
      </span>
      <span>{item.text}</span>
    </button>
  );
}

function SortableWord({
  disabled,
  item,
  status,
  workspace,
  onWorkspaceChange,
}: {
  disabled: boolean;
  item: OrderWordItem;
  onWorkspaceChange(workspace: OrderWordItem[]): void;
  status?: 'correct' | 'incorrect';
  workspace: OrderWordItem[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ disabled, id: `workspace-${item.sourceIndex}` });
  return (
    <button
      {...attributes}
      {...listeners}
      className={cn(
        'inline-flex min-h-8 items-center gap-1 rounded-full border border-border bg-card px-1.5 py-1',
        status === 'correct' &&
          'border-success bg-[var(--success-surface)] text-success',
        status === 'incorrect' &&
          'border-destructive bg-[var(--error-surface)] text-destructive',
      )}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      type="button"
      disabled={disabled}
      onClick={() =>
        onWorkspaceChange(removeOrderWord(workspace, item.sourceIndex))
      }
    >
      <span>{item.text}</span>
      {status ? (
        <span
          className={cn(
            'inline-grid size-6 shrink-0 place-items-center rounded-full border-2 border-current font-extrabold leading-none',
            status === 'correct' ? 'text-success' : 'text-destructive',
          )}
        >
          {status === 'correct' ? '✓' : '×'}
        </span>
      ) : null}
    </button>
  );
}

function WorkspaceDropSlot({ index }: { index: number }) {
  const { isOver, setNodeRef } = useDroppable({ id: `slot-${index}` });
  return (
    <span
      className={cn(
        'min-h-8 w-px shrink-0 rounded border border-dashed border-transparent',
        index === 0 && '-ms-px',
        isOver && 'border-ring',
      )}
      ref={setNodeRef}
    />
  );
}

export function OrderWordsInteraction({
  canonicalWorkspace,
  disabled = false,
  items,
  onWorkspaceChange,
  workspaceItemStatus,
  workspace,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      keyboardCodes: { cancel: ['Escape'], end: ['Space'], start: ['Space'] },
    }),
  );
  const used = new Set(workspace.map((item) => item.sourceIndex));

  function onDragEnd(event: DragEndEvent) {
    if (disabled) return;
    const activeId = String(event.active.id);
    const overId = event.over ? String(event.over.id) : null;
    if (!overId) return;
    if (activeId.startsWith('top-')) {
      const sourceIndex = Number(activeId.slice(4));
      const item = items.find((entry) => entry.sourceIndex === sourceIndex);
      if (!item || used.has(sourceIndex)) return;
      const target = overId.startsWith('slot-')
        ? Number(overId.slice(5))
        : workspace.findIndex(
            (entry) => `workspace-${entry.sourceIndex}` === overId,
          );
      onWorkspaceChange(
        insertOrderWord(
          workspace,
          item,
          target < 0 ? workspace.length : target,
        ),
      );
      return;
    }
    if (!activeId.startsWith('workspace-')) return;
    const fromIndex = workspace.findIndex(
      (entry) => `workspace-${entry.sourceIndex}` === activeId,
    );
    if (overId.startsWith('slot-')) {
      onWorkspaceChange(
        moveOrderWordToSlot(workspace, fromIndex, Number(overId.slice(5))),
      );
      return;
    }
    const target = workspace.findIndex(
      (entry) => 'workspace-' + entry.sourceIndex === overId,
    );
    if (target >= 0)
      onWorkspaceChange(reorderOrderWords(workspace, fromIndex, target));
  }
  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      sensors={sensors}
    >
      <div className="flex flex-wrap items-center gap-1">
        {items.map((item, index) => (
          <DraggableWord
            disabled={disabled}
            item={item}
            key={item.sourceIndex}
            onChoose={() => onWorkspaceChange(toggleOrderWord(workspace, item))}
            shortcut={index + 1}
            used={used.has(item.sourceIndex)}
          />
        ))}
      </div>
      <div className="flex min-h-11 flex-wrap items-center gap-0 rounded-lg border border-border bg-background p-1.5">
        <WorkspaceDropSlot index={0} />
        <SortableContext
          items={workspace.map((item) => `workspace-${item.sourceIndex}`)}
          strategy={rectSortingStrategy}
        >
          {workspace.map((item, index) => (
            <Fragment key={item.sourceIndex}>
              <SortableWord
                disabled={disabled}
                item={item}
                onWorkspaceChange={onWorkspaceChange}
                status={workspaceItemStatus?.(item, index)}
                workspace={workspace}
              />
              <WorkspaceDropSlot index={index + 1} />
            </Fragment>
          ))}
        </SortableContext>
      </div>
      {canonicalWorkspace ? (
        <div className="flex flex-wrap items-center gap-1 rounded-lg border border-success bg-[var(--success-surface)] p-1.5 text-success">
          {canonicalWorkspace.map((item) => (
            <span
              className="inline-flex min-h-8 items-center gap-1 rounded-full border border-success bg-[var(--success-surface)] px-1.5 py-1 text-success"
              key={item.sourceIndex}
            >
              <span>{item.text}</span>
              <span className="inline-grid size-6 shrink-0 place-items-center rounded-full border-2 border-current font-extrabold leading-none">
                ✓
              </span>
            </span>
          ))}
        </div>
      ) : null}
    </DndContext>
  );
}
